// ═══════════════════════════════════════════════════════════════
// linkedModel.ts – kapcsolt szervek: típusok, kapcsolattípusok, térkép-modell, közzétételi mezők
// ═══════════════════════════════════════════════════════════════
import type { LinkedEntity, LinkedEntityType, LinkedSettings, OrgUnit } from '../../types';
import type { OrgModel } from './orgModel';

export const ENTITY_TYPE_LABEL: Record<LinkedEntityType, string> = {
  hivatal: 'Polgármesteri / Közös Hivatal',
  onkormanyzat: 'Önkormányzat / Társulás',
  intezmeny: 'Intézmény (Óvoda, Iskola, Könyvtár)',
  koltsegvetesi_szerv: 'Költségvetési szerv',
  gazdalkodo_szervezet: 'Cég / Gazdasági Társaság (Kft.)',
  kozalapitvany: 'Közalapítvány / Alapítvány',
  lap: 'Lap / Kiadvány',
  egyeb: 'Egyéb Szerv',
};

/** csoportcímek a „Kapcsolt szervek” blokkban (többes szám) */
export const ENTITY_TYPE_GROUP: Record<LinkedEntityType, string> = {
  hivatal: 'Hivatalok',
  onkormanyzat: 'Önkormányzatok, társulások',
  intezmeny: 'Intézmények',
  koltsegvetesi_szerv: 'Költségvetési szervek',
  gazdalkodo_szervezet: 'Gazdálkodó szervezetek',
  kozalapitvany: 'Közalapítványok',
  lap: 'Lapok',
  egyeb: 'Egyéb szervek',
};

export const DEFAULT_LINKED_SETTINGS: LinkedSettings = {
  relation_types: [
    { key: 'fenntarto', label: 'Fenntartó' },
    { key: 'iranyito', label: 'Irányító szerv' },
    { key: 'felugyeleti', label: 'Felügyeleti szerv' },
    { key: 'tulajdonos', label: 'Tulajdonos' },
    { key: 'alapito', label: 'Alapító' },
    { key: 'tarsulasi_tag', label: 'Társulási tag' },
    { key: 'egyeb', label: 'Egyéb' },
  ],
  extra_open: false,
  map: { orientation: 'vertical', root_title: '' },
};

export const relationLabel = (settings: Pick<LinkedSettings, 'relation_types'> | null | undefined, key?: string | null) =>
  (key ? (settings?.relation_types || []).find((r) => r.key === key)?.label || key : '');

/** a térképhez: a szervek egységként (a diagram-komponens újrahasznosításához) */
export function entitiesToOrgModel(entities: LinkedEntity[]): OrgModel {
  return {
    units: entities.map((e) => ({
      id: e.id, name: e.short_name || e.name, sort_order: e.sort_order || 0,
      parent_id: e.parent_entity_id ?? null, placement: e.placement === 'beside' ? 'beside' : 'below',
    }) as OrgUnit),
    positions: [],
    persons: [],
  };
}

export function entityDescendants(entities: LinkedEntity[], id: number): Set<number> {
  const out = new Set<number>([id]);
  let grew = true;
  while (grew) {
    grew = false;
    for (const e of entities) if (e.parent_entity_id && out.has(e.parent_entity_id) && !out.has(e.id)) { out.add(e.id); grew = true; }
  }
  return out;
}

export interface ExtraField {
  key: keyof LinkedEntity;
  label: string;
  placeholder?: string;
  multiline?: boolean;
  kind?: 'text' | 'percent' | 'url' | 'phone';
}
export interface ExtraGroup {
  id: string;
  title: string;
  /** a jogszabályi hely */
  law: string;
  /** mely szervtípusoknál releváns (üres = mindegyik) */
  types: LinkedEntityType[];
  fields: ExtraField[];
}

/** Infotv. 1. melléklet I. rész – a kapcsolt szervekre vonatkozó közzétételi adatok */
export const EXTRA_GROUPS: ExtraGroup[] = [
  {
    id: 'contact', title: 'Elérhetőségek', law: '1. és 1.6. pont', types: [],
    fields: [
      { key: 'postal_address', label: 'Postai cím', placeholder: 'ha eltér a székhelytől' },
      { key: 'fax', label: 'Fax', kind: 'phone' },
      { key: 'customer_service', label: 'Ügyfélszolgálat elérhetősége', placeholder: 'felelős vezető, cím, telefon, e-mail' },
      { key: 'office_hours', label: 'Ügyfélfogadási rend', placeholder: 'pl. H–Cs 8–16, P 8–12', multiline: true },
    ],
  },
  {
    id: 'company', title: 'Gazdálkodó szervezet', law: '1.7. pont', types: ['gazdalkodo_szervezet'],
    fields: [
      { key: 'activity', label: 'Tevékenységi kör', multiline: true },
      { key: 'representative_name', label: 'Képviselő neve', placeholder: 'ha eltér a vezetőtől' },
      { key: 'share_percent', label: 'Részesedés mértéke (%)', kind: 'percent', placeholder: 'pl. 100' },
    ],
  },
  {
    id: 'foundation', title: 'Közalapítvány', law: '1.8. pont', types: ['kozalapitvany'],
    fields: [
      { key: 'founding_doc', label: 'Alapító okirat', placeholder: 'megnevezés, kelte' },
      { key: 'founding_doc_url', label: 'Alapító okirat hivatkozása', kind: 'url', placeholder: 'https://…' },
      { key: 'board_members', label: 'Kezelő szerv tagjai', placeholder: 'soronként egy név (tisztség)', multiline: true },
    ],
  },
  {
    id: 'budget', title: 'Költségvetési szerv', law: '1.9. pont', types: ['koltsegvetesi_szerv', 'intezmeny', 'hivatal'],
    fields: [
      { key: 'founding_act', label: 'Alapító jogszabály / határozat' },
      { key: 'founding_doc', label: 'Alapító okirat', placeholder: 'megnevezés, kelte' },
      { key: 'founding_doc_url', label: 'Alapító okirat hivatkozása', kind: 'url', placeholder: 'https://…' },
      { key: 'operating_license', label: 'Működési engedély', placeholder: 'szám, kiállító, kelte' },
    ],
  },
  {
    id: 'press', title: 'Lap', law: '1.10. pont', types: ['lap'],
    fields: [
      { key: 'editorial_office', label: 'Szerkesztőség neve és címe' },
      { key: 'publisher', label: 'Kiadó neve és címe' },
      { key: 'editor_in_chief', label: 'Főszerkesztő neve' },
    ],
  },
];

/** a szervtípushoz tartozó csoportok (showAll: minden csoport) */
export function extraGroupsFor(type: LinkedEntityType | undefined, showAll = false): ExtraGroup[] {
  return EXTRA_GROUPS.filter((g) => showAll || !g.types.length || (type && g.types.includes(type)));
}

/** kitöltöttség a típushoz tartozó mezőkre (egyedi mezőnként számolva) */
export function extraFillState(e: Partial<LinkedEntity>): { filled: number; total: number } {
  const keys = new Set(extraGroupsFor(e.entity_type as LinkedEntityType).flatMap((g) => g.fields.map((f) => f.key)));
  let filled = 0;
  for (const k of keys) { const v = e[k]; if (v !== undefined && v !== null && String(v).trim() !== '') filled++; }
  return { filled, total: keys.size };
}

/** az adatlap sorai (nyilvános blokk és előnézet) */
export function entityDetailRows(e: LinkedEntity): { label: string; value: string; href?: string }[] {
  const rows: { label: string; value: string; href?: string }[] = [];
  const add = (label: string, v: unknown, href?: string) => { if (v !== undefined && v !== null && String(v).trim() !== '') rows.push({ label, value: String(v), href }); };
  const http = (u?: string | null) => (u && /^https?:\/\//i.test(u) ? u : undefined);
  add('Székhely', e.address);
  add('Postai cím', e.postal_address);
  add('Telefon', e.phone, e.phone ? `tel:${e.phone}` : undefined);
  add('Fax', e.fax);
  add('E-mail', e.email, e.email ? `mailto:${e.email}` : undefined);
  add('Honlap', e.website, http(e.website));
  add('Ügyfélszolgálat', e.customer_service);
  add('Ügyfélfogadási rend', e.office_hours);
  add('Vezető', [e.leader_name, e.leader_title].filter(Boolean).join(', '));
  add('Képviselő', e.representative_name);
  add('Tevékenységi kör', e.activity);
  if (e.share_percent !== undefined && e.share_percent !== null && e.share_percent !== '') add('Részesedés', `${e.share_percent}%`);
  add('Alapító jogszabály / határozat', e.founding_act);
  if (e.founding_doc || e.founding_doc_url) add('Alapító okirat', e.founding_doc || 'megnyitás', http(e.founding_doc_url));
  add('Működési engedély', e.operating_license);
  add('Kezelő szerv tagjai', e.board_members);
  add('Szerkesztőség', e.editorial_office);
  add('Kiadó', e.publisher);
  add('Főszerkesztő', e.editor_in_chief);
  add('Törzskönyvi azonosító (PIR)', e.pir_id);
  add('Adószám', e.tax_number);
  add('Hivatali kapu', e.hivatali_kapu);
  add('KRID', e.krid);
  add('Számlaszám', e.bank_account);
  return rows;
}
